import React, { useCallback, useEffect, useState } from 'react';
import { userServices } from '../../APIServices/userServices';
import { useToast } from '../../hooks/useToast';

function TwofactorAuth({ activeItem }) {
  const toast = useToast();

  const OTP_LENGTH = 6;
  const EMPTY_OTP = Array(OTP_LENGTH).fill('');

  const [copiedField, setCopiedField] = useState(false);

  const [authType, setAuthType] = useState('apps');

  const [status, setStatus] = useState(false);
  const [appTwoFA, setAppTwoFA] = useState(false);
  const [emailTwoFA, setEmailTwoFA] = useState(false);

  const [loader, setLoader] = useState(false);

  const [updateAuthType, setUpdateAuthType] = useState(false);

  const [otp, setOtp] = useState(EMPTY_OTP);
  const [otpSent, setOtpSent] = useState(false);

  const [base64, setBase64] = useState('');
  const [secret, setSecret] = useState('');

  const [check, setCheck] = useState(false);
  const [disable, setDisable] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // enable / disable email OTP
  const [emailOtpMode, setEmailOtpMode] = useState(null);

  /**
   * ----------------------------------------------------
   * RESET STATE
   * ----------------------------------------------------
   */
  const resetTwoFactorState = useCallback(() => {
    setOtp([...EMPTY_OTP]);
    setOtpSent(false);
    setBase64('');
    setSecret('');
    setCheck(false);
    setDisable(false);
    setShowQR(false);
    setEmailOtpMode(null);
  }, []);

  /**
   * ----------------------------------------------------
   * GET CURRENT 2FA STATUS
   * ----------------------------------------------------
   */
  const getTwoFactorAuthType = useCallback(async () => {
    setLoader(true);

    try {
      const response =
        await userServices.getTwoFactorAuthType();

      const data = response?.data || {};

      setAuthType(
        data?.twoFactorAuthType || 'apps'
      );

      setStatus(Boolean(data?.status));

      setAppTwoFA(Boolean(data?.appTwoFA));

      setEmailTwoFA(Boolean(data?.emailTwoFA));
    } catch (error) {
      console.error(
        'getTwoFactorAuthType:',
        error
      );

      toast(
        error?.response?.data?.message ||
          'Unable to get 2FA configuration.',
        'warn'
      );
    } finally {
      setLoader(false);
    }
  }, [toast]);

  /**
   * ----------------------------------------------------
   * OTP INPUT
   * ----------------------------------------------------
   */
  const handleOtpInput = (value, index) => {
    const digits = value
      .replace(/\D/g, '');

    if (!digits) {
      return;
    }

    /*
     * Handles normal typing as well as
     * multiple digits entered/pasted into input.
     */
    if (digits.length > 1) {
      const newOtp = [...otp];

      digits
        .slice(0, OTP_LENGTH)
        .split('')
        .forEach((digit, offset) => {
          if (index + offset < OTP_LENGTH) {
            newOtp[index + offset] = digit;
          }
        });

      setOtp(newOtp);

      const focusIndex = Math.min(
        index + digits.length,
        OTP_LENGTH - 1
      );

      document
        .getElementById(`otp-${focusIndex}`)
        ?.focus();

      return;
    }

    const newOtp = [...otp];

    newOtp[index] = digits;

    setOtp(newOtp);

    if (index < OTP_LENGTH - 1) {
      document
        .getElementById(`otp-${index + 1}`)
        ?.focus();
    }
  };

  /**
   * ----------------------------------------------------
   * OTP BACKSPACE
   * ----------------------------------------------------
   */
  const handleOtpBackspace = (event, index) => {
    if (event.key !== 'Backspace') {
      return;
    }

    event.preventDefault();

    const newOtp = [...otp];

    if (newOtp[index]) {
      newOtp[index] = '';

      setOtp(newOtp);

      return;
    }

    if (index > 0) {
      newOtp[index - 1] = '';

      setOtp(newOtp);

      document
        .getElementById(`otp-${index - 1}`)
        ?.focus();
    }
  };

  /**
   * ----------------------------------------------------
   * OTP PASTE
   * ----------------------------------------------------
   */
  const handleOtpPaste = (event) => {
    event.preventDefault();

    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH);

    if (!pasted) {
      return;
    }

    const newOtp = [...EMPTY_OTP];

    pasted
      .split('')
      .forEach((digit, index) => {
        newOtp[index] = digit;
      });

    setOtp(newOtp);

    const focusIndex = Math.min(
      pasted.length - 1,
      OTP_LENGTH - 1
    );

    document
      .getElementById(`otp-${focusIndex}`)
      ?.focus();
  };

  /**
   * ----------------------------------------------------
   * RENDER OTP INPUTS
   * ----------------------------------------------------
   */
  const renderOtpInputs = () => (
    <div
      className="hstack"
      style={{
        justifyContent: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      {otp.map((digit, index) => (
        <input
          key={index}
          id={`otp-${index}`}
          type="text"
          inputMode="numeric"
          autoComplete={
            index === 0
              ? 'one-time-code'
              : 'off'
          }
          maxLength={1}
          value={digit}
          className="inp text-center"
          style={{
            width: 42,
            textAlign: 'center',
          }}
          disabled={loader}
          onChange={(event) =>
            handleOtpInput(
              event.target.value,
              index
            )
          }
          onPaste={handleOtpPaste}
          onKeyDown={(event) =>
            handleOtpBackspace(
              event,
              index
            )
          }
        />
      ))}
    </div>
  );

  /**
   * ----------------------------------------------------
   * CHANGE 2FA TYPE
   * ----------------------------------------------------
   */
  const twoFactorAuthType = async () => {
    setLoader(true);

    try {
      const response =
        await userServices.twoFactorAuthType({
          type: authType,
        });

      if (response?.statusCode === 200) {
        resetTwoFactorState();

        setUpdateAuthType(true);

        await getTwoFactorAuthType();
      } else {
        toast(
          response?.message ||
            'Unable to update 2FA type.',
          'warn'
        );
      }
    } catch (error) {
      console.error(
        'twoFactorAuthType:',
        error
      );

      toast(
        error?.response?.data?.message ||
          'Unable to update 2FA type.',
        'warn'
      );
    } finally {
      setLoader(false);
    }
  };

  /**
   * ----------------------------------------------------
   * SET QR RESPONSE
   * ----------------------------------------------------
   *
   * Supports possible API responses:
   *
   * {
   *   data: "data:image/png;base64,..."
   * }
   *
   * OR
   *
   * {
   *   data: {
   *     base64: "...",
   *     secret: "..."
   *   }
   * }
   */
  const setQRResponse = (response) => {
    const responseData = response?.data;

    let qrBase64 = '';
    let qrSecret = '';

    if (
      typeof responseData === 'string'
    ) {
      qrBase64 = responseData;
    } else if (
      responseData &&
      typeof responseData === 'object'
    ) {
      qrBase64 =
        responseData?.base64 ||
        responseData?.qrCode ||
        responseData?.qr ||
        '';

      qrSecret =
        responseData?.secret ||
        '';
    }

    if (!qrBase64) {
      qrBase64 =
        response?.base64 ||
        response?.qrCode ||
        response?.qr ||
        '';
    }

    if (!qrSecret) {
      qrSecret =
        response?.secret ||
        '';
    }

    setBase64(qrBase64);
    setSecret(qrSecret);

    return {
      qrBase64,
      qrSecret,
    };
  };

  /**
   * ----------------------------------------------------
   * GENERATE QR
   * ----------------------------------------------------
   */
  const generateQRCode = async () => {
    setLoader(true);

    try {
      const response =
        await userServices.generateQRCode();

      const {
        qrBase64,
        qrSecret,
      } = setQRResponse(response);

      if (!qrBase64) {
        toast(
          'QR code was not returned by server.',
          'warn'
        );

        return;
      }

      if (!qrSecret) {
        toast(
          '2FA secret was not returned by server.',
          'warn'
        );
      }
    } catch (error) {
      console.error(
        'generateQRCode:',
        error
      );

      toast(
        error?.response?.data?.message ||
          'Unable to generate QR code.',
        'warn'
      );
    } finally {
      setLoader(false);
    }
  };

  /**
   * ----------------------------------------------------
   * SHOW EXISTING QR
   * ----------------------------------------------------
   */
  const showQRCode = async () => {
    setLoader(true);

    try {
      const response =
        await userServices.showQRCode();

      const {
        qrBase64,
        qrSecret,
      } = setQRResponse(response);

      if (!qrBase64) {
        toast(
          'QR code was not returned by server.',
          'warn'
        );

        return null;
      }

      setShowQR(true);

      return {
        qrBase64,
        qrSecret,
      };
    } catch (error) {
      console.error(
        'showQRCode:',
        error
      );

      toast(
        error?.response?.data?.message ||
          'Unable to load QR code.',
        'warn'
      );

      return null;
    } finally {
      setLoader(false);
    }
  };

  /**
   * ----------------------------------------------------
   * VERIFY QR
   * ----------------------------------------------------
   */
  const checkQRCode = async () => {
    const otpValue = otp.join('');

    if (otpValue.length !== OTP_LENGTH) {
      toast(
        'Please enter a 6-digit OTP.',
        'warn'
      );

      return false;
    }

    if (!secret) {
      toast(
        '2FA secret is missing.',
        'warn'
      );

      return false;
    }

    setLoader(true);

    try {
      const response =
        await userServices.checkQRCode({
          secret,
          otp: otpValue,
        });

      if (
        response?.statusCode === 200
      ) {
        toast(
          'QR code verified successfully.',
          'ok'
        );

        return true;
      }

      toast(
        response?.message ||
          'OTP verification failed.',
        'warn'
      );

      return false;
    } catch (error) {
      console.error(
        'checkQRCode:',
        error
      );

      toast(
        error?.response?.data?.message ||
          'OTP verification failed.',
        'warn'
      );

      return false;
    } finally {
      setLoader(false);
    }
  };

  /**
   * ----------------------------------------------------
   * ENABLE APP 2FA
   * ----------------------------------------------------
   */
  const enableTwoFactorAuth = async () => {
    const otpValue = otp.join('');

    if (otpValue.length !== OTP_LENGTH) {
      toast(
        'Please enter a 6-digit OTP.',
        'warn'
      );

      return;
    }

    if (!secret) {
      toast(
        '2FA secret is missing.',
        'warn'
      );

      return;
    }

    setLoader(true);

    try {
      const response =
        await userServices.enableTwoFactorAuth({
          secret,
          otp: otpValue,
        });

      if (
        response?.statusCode === 200
      ) {
        toast(
          'App 2FA enabled successfully.',
          'ok'
        );

        resetTwoFactorState();

        setUpdateAuthType(false);

        await getTwoFactorAuthType();
      } else {
        toast(
          response?.message ||
            'Unable to enable app 2FA.',
          'warn'
        );
      }
    } catch (error) {
      console.error(
        'enableTwoFactorAuth:',
        error
      );

      toast(
        error?.response?.data?.message ||
          'Unable to enable app 2FA.',
        'warn'
      );
    } finally {
      setLoader(false);
    }
  };

  /**
   * ----------------------------------------------------
   * DISABLE APP 2FA
   * ----------------------------------------------------
   */
  const disableTwoFactorAuth = async () => {
    const otpValue = otp.join('');

    if (otpValue.length !== OTP_LENGTH) {
      toast(
        'Please enter a 6-digit OTP.',
        'warn'
      );

      return;
    }

    if (!secret) {
      toast(
        '2FA secret is missing.',
        'warn'
      );

      return;
    }

    setLoader(true);

    try {
      const response =
        await userServices.disableTwoFactorAuth({
          secret,
          otp: otpValue,
        });

      if (
        response?.statusCode === 200
      ) {
        toast(
          'App 2FA disabled successfully.',
          'ok'
        );

        resetTwoFactorState();

        setUpdateAuthType(false);

        await getTwoFactorAuthType();
      } else {
        toast(
          response?.message ||
            'Unable to disable app 2FA.',
          'warn'
        );
      }
    } catch (error) {
      console.error(
        'disableTwoFactorAuth:',
        error
      );

      toast(
        error?.response?.data?.message ||
          'Unable to disable app 2FA.',
        'warn'
      );
    } finally {
      setLoader(false);
    }
  };

  /**
   * ----------------------------------------------------
   * SEND EMAIL OTP
   * ----------------------------------------------------
   */
  const sendOTPForEmailTwoFa = async (
    mode = 'enable'
  ) => {
    setLoader(true);

    try {
      const response =
        await userServices.emailOTPFor2FA();

      if (
        response?.statusCode === 200
      ) {
        setEmailOtpMode(mode);

        setOtpSent(true);

        setOtp([...EMPTY_OTP]);

        toast(
          'OTP sent to your registered email.',
          'ok'
        );
      } else {
        toast(
          response?.message ||
            'Unable to send OTP.',
          'warn'
        );
      }
    } catch (error) {
      console.error(
        'emailOTPFor2FA:',
        error
      );

      toast(
        error?.response?.data?.message ||
          'Unable to send OTP.',
        'warn'
      );
    } finally {
      setLoader(false);
    }
  };

  /**
   * ----------------------------------------------------
   * ENABLE EMAIL 2FA
   * ----------------------------------------------------
   */
  const enableTwoFactorAuthByEmail =
    async () => {
      const otpValue = otp.join('');

      if (
        otpValue.length !== OTP_LENGTH
      ) {
        toast(
          'Please enter a 6-digit OTP.',
          'warn'
        );

        return;
      }

      setLoader(true);

      try {
        const response =
          await userServices.enableTwoFactorAuthByEmail(
            {
              otp: otpValue,
            }
          );

        if (
          response?.statusCode === 200
        ) {
          toast(
            'Email 2FA enabled successfully.',
            'ok'
          );

          resetTwoFactorState();

          setUpdateAuthType(false);

          await getTwoFactorAuthType();
        } else {
          toast(
            response?.message ||
              'Unable to enable email 2FA.',
            'warn'
          );
        }
      } catch (error) {
        console.error(
          'enableTwoFactorAuthByEmail:',
          error
        );

        toast(
          error?.response?.data?.message ||
            'Unable to enable email 2FA.',
          'warn'
        );
      } finally {
        setLoader(false);
      }
    };

  /**
   * ----------------------------------------------------
   * DISABLE EMAIL 2FA
   * ----------------------------------------------------
   */
  const disableTwoFactorAuthByEmail =
    async () => {
      const otpValue = otp.join('');

      if (
        otpValue.length !== OTP_LENGTH
      ) {
        toast(
          'Please enter a 6-digit OTP.',
          'warn'
        );

        return;
      }

      setLoader(true);

      try {
        const response =
          await userServices.disableTwoFactorAuthByEmail(
            {
              otp: otpValue,
            }
          );

        if (
          response?.statusCode === 200
        ) {
          toast(
            'Email 2FA disabled successfully.',
            'ok'
          );

          resetTwoFactorState();

          setUpdateAuthType(false);

          await getTwoFactorAuthType();
        } else {
          toast(
            response?.message ||
              'Unable to disable email 2FA.',
            'warn'
          );
        }
      } catch (error) {
        console.error(
          'disableTwoFactorAuthByEmail:',
          error
        );

        toast(
          error?.response?.data?.message ||
            'Unable to disable email 2FA.',
          'warn'
        );
      } finally {
        setLoader(false);
      }
    };

  /**
   * ----------------------------------------------------
   * START APP 2FA DISABLE
   * ----------------------------------------------------
   */
  const getQRDataForDisable =
    async () => {
      const result =
        await showQRCode();

      if (result?.qrSecret) {
        setDisable(true);
      }
    };

  /**
   * ----------------------------------------------------
   * COPY SECRET
   * ----------------------------------------------------
   */
  const handleCopy = async (text) => {
    if (!text) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        text
      );

      setCopiedField(true);

      setTimeout(() => {
        setCopiedField(false);
      }, 1500);
    } catch (error) {
      console.error(
        'Clipboard error:',
        error
      );

      toast(
        'Unable to copy secret.',
        'warn'
      );
    }
  };

  /**
   * ----------------------------------------------------
   * INITIAL LOAD
   * ----------------------------------------------------
   */
  useEffect(() => {
    if (!activeItem) {
      return;
    }

    resetTwoFactorState();

    setUpdateAuthType(false);

    getTwoFactorAuthType();
  }, [
    activeItem,
    getTwoFactorAuthType,
    resetTwoFactorState,
  ]);

  /**
   * ----------------------------------------------------
   * COMMON BACK BUTTON
   * ----------------------------------------------------
   */
  const handleBackToAuthType = () => {
    resetTwoFactorState();
    setUpdateAuthType(false);
  };

  /**
   * ----------------------------------------------------
   * RENDER
   * ----------------------------------------------------
   */
  return (
    <div>
      {/* PAGE HEADER */}
      <div className="page-header">
        <div>
          <div className="page-eyebrow">
            Manage Secure Account
          </div>

          <h1 className="page-title">
            Two Factor Authentication
          </h1>

          <p className="page-sub">
            Strengthen your account security by
            enabling two-factor authentication
            (2FA). Add an extra layer of
            protection by requiring a
            verification code during sign-in.
          </p>
        </div>
      </div>

      {/* SECTION TITLE */}
      <div className="flex between center mb-3">
        <div className="section-label">
          Two Factor Authentication
        </div>
      </div>

      {/* MAIN CARD */}
      <div className="card card-pad">

        {/* CURRENT STATUS */}
        <div className="field">
          <label className="field-label">
            Current Status{' '}

            <span
              className={`tag ${
                status
                  ? 'tag-ok'
                  : 'tag-danger'
              }`}
            >
              {status
                ? 'Enabled'
                : 'Disabled'}
            </span>
          </label>
        </div>

        {/* =================================================
            SELECT AUTHENTICATION TYPE
        ================================================== */}
        {!updateAuthType ? (
          <>
            <div className="field">
              <label className="field-label">
                Authentication Type
              </label>

              <select
                className="inp"
                value={authType}
                onChange={(event) => {
                  const value =
                    event.target.value;

                  setAuthType(value);

                  resetTwoFactorState();
                }}
                disabled={loader}
              >
                <option value="apps">
                  By Apps
                </option>

                <option value="email">
                  By Email
                </option>
              </select>
            </div>

            <div
              className="flex between center"
              style={{ gap: 12 }}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={
                  twoFactorAuthType
                }
                disabled={loader}
              >
                {loader
                  ? 'Processing...'
                  : 'Next'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* =================================================
                APP AUTHENTICATION
            ================================================== */}
            {authType === 'apps' ? (
              <>
                {/* ============================================
                    APP 2FA DISABLED / ENABLE FLOW
                ============================================= */}
                {!appTwoFA ? (
                  <>
                    {/* QR STEP */}
                    {!check ? (
                      <>
                        <div className="field text-center">

                          {base64 ? (
                            <>
                              <div
                                className="card card-pad"
                                style={{
                                  maxWidth: 180,
                                  margin:
                                    '0 auto',
                                }}
                              >
                                <img
                                  src={base64}
                                  alt="2FA QR Code"
                                  style={{
                                    width: '100%',
                                    display:
                                      'block',
                                  }}
                                />
                              </div>

                              {secret && (
                                <div
                                  className="hstack"
                                  style={{
                                    justifyContent:
                                      'center',
                                    alignItems:
                                      'center',
                                    gap: 10,
                                    marginTop: 12,
                                    flexWrap:
                                      'wrap',
                                  }}
                                >
                                  <span
                                    style={{
                                      wordBreak:
                                        'break-all',
                                    }}
                                  >
                                    {secret}
                                  </span>

                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() =>
                                      handleCopy(
                                        secret
                                      )
                                    }
                                    disabled={
                                      loader
                                    }
                                  >
                                    {copiedField
                                      ? 'Copied'
                                      : 'Copy'}
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            <div
                              className="tag tag-danger"
                              style={{
                                width: 180,
                                margin:
                                  '0 auto',
                              }}
                            >
                              QR not generated
                              yet
                            </div>
                          )}
                        </div>

                        <div
                          className="flex between center"
                          style={{
                            gap: 12,
                            flexWrap:
                              'wrap',
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={
                              handleBackToAuthType
                            }
                            disabled={loader}
                          >
                            Back
                          </button>

                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={
                              generateQRCode
                            }
                            disabled={loader}
                          >
                            {loader
                              ? 'Processing...'
                              : base64
                              ? 'Re-Generate QR'
                              : 'Generate QR'}
                          </button>

                          {base64 && (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() =>
                                setCheck(
                                  true
                                )
                              }
                              disabled={loader}
                            >
                              Next
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      /* OTP VERIFICATION */
                      <>
                        <div className="field text-center">
                          <label className="field-label">
                            Verification Code
                          </label>

                          <p
                            className="page-sub"
                            style={{
                              marginBottom: 15,
                            }}
                          >
                            Enter the 6-digit
                            code from your
                            authenticator
                            application.
                          </p>

                          {renderOtpInputs()}
                        </div>

                        <div
                          className="flex between center"
                          style={{
                            gap: 12,
                            flexWrap:
                              'wrap',
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => {
                              setCheck(
                                false
                              );

                              setOtp([
                                ...EMPTY_OTP,
                              ]);
                            }}
                            disabled={loader}
                          >
                            Back
                          </button>

                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={
                              enableTwoFactorAuth
                            }
                            disabled={
                              loader ||
                              otp.join(
                                ''
                              ).length !==
                                OTP_LENGTH
                            }
                          >
                            {loader
                              ? 'Processing...'
                              : 'Verify and Enable'}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  /* ==========================================
                     APP 2FA ENABLED
                  =========================================== */
                  <>
                    {!disable ? (
                      <>
                        {!showQR ? (
                          <>
                            <div className="field text-center">
                              <div
                                className="tag tag-ok"
                                style={{
                                  display:
                                    'inline-flex',
                                  justifyContent:
                                    'center',
                                }}
                              >
                                Enabled (2FA by
                                Apps)
                              </div>
                            </div>

                            <div
                              className="flex between center"
                              style={{
                                gap: 12,
                                flexWrap:
                                  'wrap',
                              }}
                            >
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={
                                  showQRCode
                                }
                                disabled={
                                  loader
                                }
                              >
                                {loader
                                  ? 'Loading...'
                                  : 'Show QR'}
                              </button>

                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={
                                  getQRDataForDisable
                                }
                                disabled={
                                  loader
                                }
                              >
                                Disable
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="field text-center">
                              {base64 && (
                                <>
                                  <div
                                    className="card card-pad"
                                    style={{
                                      maxWidth: 180,
                                      margin:
                                        '0 auto',
                                    }}
                                  >
                                    <img
                                      src={
                                        base64
                                      }
                                      alt="2FA QR Code"
                                      style={{
                                        width:
                                          '100%',
                                        display:
                                          'block',
                                      }}
                                    />
                                  </div>

                                  {secret && (
                                    <div
                                      className="hstack"
                                      style={{
                                        justifyContent:
                                          'center',
                                        alignItems:
                                          'center',
                                        gap: 10,
                                        marginTop: 12,
                                        flexWrap:
                                          'wrap',
                                      }}
                                    >
                                      <span
                                        style={{
                                          wordBreak:
                                            'break-all',
                                        }}
                                      >
                                        {secret}
                                      </span>

                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={() =>
                                          handleCopy(
                                            secret
                                          )
                                        }
                                        disabled={
                                          loader
                                        }
                                      >
                                        {copiedField
                                          ? 'Copied'
                                          : 'Copy'}
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            <div
                              className="flex between center"
                              style={{
                                gap: 12,
                                flexWrap:
                                  'wrap',
                              }}
                            >
                              <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() =>
                                  setShowQR(
                                    false
                                  )
                                }
                                disabled={
                                  loader
                                }
                              >
                                Hide QR
                              </button>

                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={
                                  getQRDataForDisable
                                }
                                disabled={
                                  loader
                                }
                              >
                                Disable
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      /* ========================================
                         DISABLE APP 2FA OTP
                      ========================================= */
                      <>
                        <div className="field text-center">
                          <label className="field-label">
                            Verification Code
                          </label>

                          <p
                            className="page-sub"
                            style={{
                              marginBottom: 15,
                            }}
                          >
                            Enter the 6-digit
                            code from your
                            authenticator
                            application to
                            disable 2FA.
                          </p>

                          {renderOtpInputs()}
                        </div>

                        <div
                          className="flex between center"
                          style={{
                            gap: 12,
                            flexWrap:
                              'wrap',
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => {
                              setDisable(
                                false
                              );

                              setOtp([
                                ...EMPTY_OTP,
                              ]);
                            }}
                            disabled={loader}
                          >
                            Back
                          </button>

                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={
                              disableTwoFactorAuth
                            }
                            disabled={
                              loader ||
                              otp.join(
                                ''
                              ).length !==
                                OTP_LENGTH
                            }
                          >
                            {loader
                              ? 'Processing...'
                              : 'Disable 2FA'}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              /* =================================================
                 EMAIL AUTHENTICATION
              ================================================== */
              <>
                {!emailTwoFA ? (
                  <>
                    {/* EMAIL 2FA DISABLED */}
                    {!otpSent ? (
                      <>
                        <div className="field text-center">
                          <div
                            className="tag tag-danger"
                            style={{
                              display:
                                'inline-flex',
                              justifyContent:
                                'center',
                            }}
                          >
                            Disabled
                          </div>

                          <p
                            className="page-sub"
                            style={{
                              marginTop: 12,
                            }}
                          >
                            To enable Email
                            2FA, send OTP to
                            your registered
                            email address.
                          </p>
                        </div>

                        <div
                          className="flex between center"
                          style={{
                            gap: 12,
                            flexWrap:
                              'wrap',
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={
                              handleBackToAuthType
                            }
                            disabled={loader}
                          >
                            Back
                          </button>

                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() =>
                              sendOTPForEmailTwoFa(
                                'enable'
                              )
                            }
                            disabled={loader}
                          >
                            {loader
                              ? 'Sending...'
                              : 'Send OTP'}
                          </button>
                        </div>
                      </>
                    ) : (
                      /* EMAIL ENABLE OTP */
                      <>
                        <div className="field text-center">
                          <label className="field-label">
                            Verification Code
                          </label>

                          <p
                            className="page-sub"
                            style={{
                              marginBottom: 15,
                            }}
                          >
                            Enter the OTP sent
                            to your registered
                            email address.
                          </p>

                          {renderOtpInputs()}
                        </div>

                        <div
                          className="flex between center"
                          style={{
                            gap: 12,
                            flexWrap:
                              'wrap',
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => {
                              setOtpSent(
                                false
                              );

                              setOtp([
                                ...EMPTY_OTP,
                              ]);

                              setEmailOtpMode(
                                null
                              );
                            }}
                            disabled={loader}
                          >
                            Back
                          </button>

                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={
                              enableTwoFactorAuthByEmail
                            }
                            disabled={
                              loader ||
                              otp.join(
                                ''
                              ).length !==
                                OTP_LENGTH
                            }
                          >
                            {loader
                              ? 'Processing...'
                              : 'Verify with Enable'}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  /* ==========================================
                     EMAIL 2FA ENABLED
                  =========================================== */
                  <>
                    <div className="field text-center">
                      <div
                        className="tag tag-ok"
                        style={{
                          display:
                            'inline-flex',
                          justifyContent:
                            'center',
                        }}
                      >
                        Enabled (2FA by Email)
                      </div>
                    </div>

                    {!otpSent ? (
                      <div
                        className="flex between center"
                        style={{
                          gap: 12,
                          flexWrap:
                            'wrap',
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={
                            handleBackToAuthType
                          }
                          disabled={loader}
                        >
                          Back
                        </button>

                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() =>
                            sendOTPForEmailTwoFa(
                              'disable'
                            )
                          }
                          disabled={loader}
                        >
                          {loader
                            ? 'Sending...'
                            : 'Disable'}
                        </button>
                      </div>
                    ) : (
                      /* EMAIL DISABLE OTP */
                      <>
                        <div className="field text-center">
                          <label className="field-label">
                            Verification Code
                          </label>

                          <p
                            className="page-sub"
                            style={{
                              marginBottom: 15,
                            }}
                          >
                            Enter the OTP sent
                            to your registered
                            email address to
                            disable Email
                            2FA.
                          </p>

                          {renderOtpInputs()}
                        </div>

                        <div
                          className="flex between center"
                          style={{
                            gap: 12,
                            flexWrap:
                              'wrap',
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => {
                              setOtpSent(
                                false
                              );

                              setOtp([
                                ...EMPTY_OTP,
                              ]);

                              setEmailOtpMode(
                                null
                              );
                            }}
                            disabled={loader}
                          >
                            Back
                          </button>

                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={
                              disableTwoFactorAuthByEmail
                            }
                            disabled={
                              loader ||
                              otp.join(
                                ''
                              ).length !==
                                OTP_LENGTH
                            }
                          >
                            {loader
                              ? 'Processing...'
                              : 'Disable 2FA'}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TwofactorAuth;